import React, { useEffect, useState } from "react";
import { observer, inject } from "mobx-react";

import Controls from "../Components/Controls";
import MediaSessionManager from "../Utils/MediaSessionManager";
import { volumeStorage } from "../Utils/volumeStorage";
import { usePlausible } from "../hooks/usePlausible";
import { showToast } from "../Utils/toast";
import {
  activeSide as djActiveSide,
  togglePlayPause as djTogglePlayPause,
  seekActive as djSeek,
} from "../Utils/djAudioController";

function Playback({ PlaybackStore, RecentUsersStore, PlaylistStore, DjStore, onScrollToCurrentTrack }) {
  const [regularPos, setRegularPos] = useState(0);
  const { trackEvent } = usePlausible();

  const djHasLoaded =
    !!(DjStore?.deckA?.track || DjStore?.deckB?.track);
  const dominantSide = DjStore ? djActiveSide(DjStore.crossfade) : "A";
  const dominantDeck = DjStore ? (dominantSide === "A" ? DjStore.deckA : DjStore.deckB) : null;
  const djActive =
    djHasLoaded &&
    (DjStore.deckA.isPlaying ||
      DjStore.deckB.isPlaying ||
      DjStore.queueMode ||
      DjStore.autoTransition);

  // Event-based position tracking for regular playback
  useEffect(() => {
    if (djActive) return;
    const audioElement = document.getElementById("DOMAudioPlayer");
    if (!audioElement) return;
    const handleTimeUpdate = () => setRegularPos(audioElement.currentTime);
    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    return () => audioElement.removeEventListener('timeupdate', handleTimeUpdate);
  }, [djActive]);

  // Initialize audio element volume from localStorage
  useEffect(() => {
    const audioElement = document.getElementById("DOMAudioPlayer");
    if (audioElement) {
      audioElement.volume = volumeStorage.get();
    }
  }, []);

  const controlsTrack = djActive ? dominantDeck?.track : PlaybackStore.currentTrack;
  const controlsIsPlaying = djActive ? dominantDeck?.isPlaying : PlaybackStore.isPlaying;
  const controlsPosition = djActive ? (dominantDeck?.currentTimeMs ?? 0) / 1000 : regularPos;

  return (
    <div className="w-full">
      <Controls
        isPlaying={controlsIsPlaying}
        track={controlsTrack}
        onPlayPause={() => {
          if (djActive) {
            djTogglePlayPause(DjStore.crossfade);
            return;
          }
          const wasPlaying = PlaybackStore.isPlaying;
          PlaybackStore.togglePlay();
          trackEvent(wasPlaying ? 'Pause Track' : 'Play Track', {
            mediaId: PlaybackStore.currentTrack?.mediaId,
            title: PlaybackStore.currentTrack?.title,
          });
        }}
        onNext={() => {
          if (djActive) return;
          PlaybackStore.next();
          trackEvent('Next Track', {
            mediaId: PlaybackStore.currentTrack?.mediaId,
          });
        }}
        onPrevious={() => {
          if (djActive) return;
          PlaybackStore.previous();
          trackEvent('Previous Track', {
            mediaId: PlaybackStore.currentTrack?.mediaId,
          });
        }}
        shuffleEnabled={PlaybackStore.shuffleEnabled}
        onShuffle={() => {
          if (djActive) return;
          PlaybackStore.toggleShuffle();
          trackEvent('Toggle Shuffle', {
            enabled: !PlaybackStore.shuffleEnabled,
          });
        }}
        onVolumeChange={(volume) => {
          const audioElement = document.getElementsByTagName("audio")[0];
          if (audioElement) audioElement.volume = volume;
        }}
        onSeek={(time) => {
          if (djActive) {
            djSeek(DjStore.crossfade, time);
            return;
          }
          PlaybackStore.setCurrentTime(time);
        }}
        currentPosition={controlsPosition}
        currentUser={RecentUsersStore.currentUser}
        onDelete={(mediaId) => {
          if (djActive) return;
          PlaybackStore.deleteSong(mediaId);
          trackEvent('Delete Song', { mediaId });
        }}
        currentPlaylistId={PlaylistStore.currentViewingPlaylistId}
        onScrollToCurrentTrack={onScrollToCurrentTrack}
      />
      <div className="player-container">
        <audio
          id="DOMAudioPlayer"
          preload="none"
          src={
            PlaybackStore.currentTrack
              ? PlaybackStore.currentTrack.audioUrl
              : ""
          }
          onEnded={() => {
            console.log('Track ended, advancing to next');
            trackEvent('Track Completed', {
              mediaId: PlaybackStore.currentTrack?.mediaId,
              title: PlaybackStore.currentTrack?.title,
            });
            PlaybackStore.next();
          }}
          onPlay={() => {
            console.log('Audio started playing');
            PlaybackStore.isPlaying = true;
            MediaSessionManager.updatePlaybackState('playing');
          }}
          onPause={() => {
            console.log('Audio paused');
            PlaybackStore.isPlaying = false;
            MediaSessionManager.updatePlaybackState('paused');
          }}
          onTimeUpdate={(e) => {
            const audio = e.target;
            if (audio.duration && !isNaN(audio.duration)) {
              MediaSessionManager.updatePositionState(
                audio.duration,
                audio.currentTime,
                audio.playbackRate
              );
            }
          }}
          onLoadedMetadata={(e) => {
            const audio = e.target;
            console.log('Audio metadata loaded, duration:', audio.duration);
            MediaSessionManager.updatePositionState(audio.duration, 0);
          }}
          onError={(e) => {
            const err = e.target.error;
            console.error('Audio playback error:', err);
            const track = PlaybackStore.currentTrack;
            const codes = { 1: "Playback aborted", 2: "Network error", 3: "Decode error", 4: "Source not supported" };
            const message = codes[err?.code] || err?.message || "Playback failed";
            showToast.error(`${message}${track?.title ? ` — "${track.title}"` : ''}`);
          }}
        />
      </div>
    </div>
  );
}

export default inject("PlaybackStore", "RecentUsersStore", "PlaylistStore", "DjStore")(observer(Playback));
